import React, { useState } from "react";
import { Phone, MessageSquare, Check } from "lucide-react";
import { LOCATIONS, SERVICES } from "../data/site";
import { Seo } from "../components/Seo";
import { SEO, contactPageSchema, breadcrumbSchema } from "../data/seo";

export const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", location: "", service: "", message: "", appointmentDateTime: "", preferred: "phone",
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title={SEO.contact.title}
        description={SEO.contact.description}
        schemas={[
          contactPageSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
        ]}
      />
      <section className="pt-32 md:pt-40 pb-16 container-x">
        <div className="max-w-3xl">
          <div className="label-eyebrow mb-6">Contact Us</div>
          <h1 className="heading-display text-5xl md:text-7xl">
            Let's start a <span className="italic text-gold">conversation.</span>
          </h1>
          <p className="text-ink/70 mt-8 text-lg max-w-2xl leading-relaxed">
            Tell us a little about you and what you're looking for. A member of our team will respond within one business day — usually within a few hours.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 container-x">
        <div className="max-w-5xl">
            {submitted ? (
              <div className="bg-ink text-white p-12 reveal" data-testid="contact-success">
                <Check size={36} strokeWidth={1} className="text-gold mb-6" />
                <h2 className="font-serif text-4xl font-light">Thank you, {form.name || "friend"}.</h2>
                <p className="text-white/70 mt-4 leading-relaxed">Your message is on its way to our team. We'll reach out shortly via your preferred contact method.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", location: "", service: "", message: "", appointmentDateTime: "", preferred: "phone" }); }} className="btn-outline-light mt-10" data-testid="contact-reset">Send another message</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Input label="Name" name="name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="contact-input-name" />
                  <Input label="Email" name="email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="contact-input-email" />
                  <Input label="Phone" name="phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testId="contact-input-phone" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Select label="Service of Interest" value={form.service} onChange={(v) => setForm({ ...form, service: v })} options={[{ value: "", label: "Select a service…" }, ...SERVICES.map((s) => ({ value: s.slug, label: s.name })), { value: "general", label: "General Inquiry" }]} required testId="contact-select-service" />
                  <Input label=" Date & Time" name="appointmentDateTime" type="datetime-local" value={form.appointmentDateTime} onChange={(v) => setForm({ ...form, appointmentDateTime: v })} testId="contact-input-datetime" />
                  <div>
                    <FieldLabel label="Contact Method" required />
                    <select
                      value={form.preferred}
                      onChange={(e) => setForm({ ...form, preferred: e.target.value })}
                      required
                      aria-hidden="true"
                      tabIndex={-1}
                      className="sr-only"
                    >
                      <option value="">Select  contact method</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="text">Text</option>
                    </select>
                    <div className="flex gap-3 flex-wrap">
                      {["email", "phone", "text"].map((m) => (
                        <button
                          type="button"
                          key={m}
                          onClick={() => setForm({ ...form, preferred: m })}
                          data-testid={`contact-pref-${m}`}
                          className={`px-5 py-2.5 text-[11px] tracking-[0.3em] uppercase border transition-colors duration-300 ${
                            form.preferred === m ? "bg-ink text-white border-ink" : "border-ink/20 hover:border-ink"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <RadioGroup
                    label="Preferred Location"
                    options={LOCATIONS.map((location) => ({
                      value: location.slug,
                      label: `${location.name} — ${location.city}`,
                      address: location.address,
                      phone: location.phone,
                      email: location.email,
                    }))}
                    value={form.location}
                    onChange={(value) => setForm({ ...form, location: value })}
                    name="preferred-location"
                    required
                    testId="contact-radio-location"
                  />
                </div>
                <div>
                  <FieldLabel label="Your Message" required />
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows="5"
                    required
                    data-testid="contact-input-message"
                    className="w-full bg-transparent border-b border-ink/20 focus:border-gold outline-none py-3 text-lg font-light resize-none transition-colors"
                    placeholder="Tell us a bit about what you're hoping for…"
                  />
                </div>
                <button type="submit" data-testid="contact-submit" className="btn-primary mt-4">Send Message</button>
              </form>
            )}
        </div>
      </section>
    </div>
  );
};



const FieldLabel = ({ label, required, htmlFor }) => (
  <label className="label-eyebrow block mb-3" htmlFor={htmlFor}>
    {label}
    {required && <span className="ml-1 text-red-600">*</span>}
  </label>
);


const Input = ({ label, name, type = "text", value, onChange, required, testId }) => (
  <div>
    <FieldLabel label={label} required={required} htmlFor={name} />
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      data-testid={testId}
      className="w-full bg-transparent border-b border-ink/20 focus:border-gold outline-none py-3 text-lg font-light transition-colors"
    />
  </div>
);

const Select = ({ label, value, onChange, options, required, testId }) => (
  <div>
    <FieldLabel label={label} required={required} />
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      data-testid={testId}
      className="w-full bg-transparent border-b border-ink/20 focus:border-gold outline-none py-3 text-lg font-light transition-colors appearance-none cursor-pointer"
    >
      {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  </div>
);

const RadioGroup = ({ label, options, value, onChange, name, required, testId }) => (
  <fieldset>
    <legend className="label-eyebrow block mb-3">
      {label}
      {required && <span className="ml-1 text-red-600">*</span>}
    </legend>
    <div className="space-y-3">
      {options.map((option) => {
        const checked = value === option.value;

        return (
          <label
            key={option.value}
            className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${
              checked ? "border-gold bg-gold/10" : "border-ink/15 hover:border-gold/50"
            }`}
            data-testid={`${testId}-${option.value}`}
          >
            <input
              type="radio"
              name={name}
              required={required}
              checked={checked}
              onChange={() => onChange(option.value)}
              className="h-4 w-4 accent-[#B89968]"
            />
            <span className="flex flex-col">
              <span className="text-sm md:text-base text-ink/80">{option.label}</span>
              {option.address && (
                <span className="text-xs text-ink/60 mt-0.5">{option.address}</span>
              )}
              {(option.phone || option.email) && (
                <span className="flex flex-wrap items-center gap-3 mt-1.5 text-xs">
                  {option.phone && (
                    <>
                      <a
                        href={`tel:${option.phone.replace(/\D/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-ink/75 hover:text-gold transition-colors"
                        aria-label={`Call ${option.phone}`}
                      >
                        <Phone size={12} strokeWidth={1.8} />
                        {option.phone}
                      </a>
                      <a
                        href={`sms:${option.phone.replace(/\D/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-gold hover:text-gold-dark transition-colors"
                        aria-label={`Message ${option.phone}`}
                      >
                        <MessageSquare size={12} strokeWidth={1.8} />
                        
                      </a>
                    </>
                  )}
                  {option.email && (
                    <a
                      href={`mailto:${option.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-ink/75 hover:text-gold transition-colors"
                    >
                      {option.email}
                    </a>
                  )}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

